// Forum Thread Types - Matches Backend DTOs

// Sort Options for threads
export type ThreadSortBy = 'latest' | 'popular' | 'most_discussed';

// Standard API Response wrapper
export interface APIResponse<T> {
  success: boolean;
  code: number;
  description: string;
  data: T;
  meta_data?: {
    generated_at?: string;
    tenant_key?: string;
    course_id?: string;
    filters_applied?: Record<string, any>;
  };
  error?: {
    error_code?: string;
    error_description?: string;
  };
}

// Thread Creator Information (populated field)
export interface ThreadCreator {
  _id: string;
  given_name: string;
  family_name: string;
  profile_picture?: string;
}

// Forum Thread Response DTO (matches ForumThreadResponseDto from backend)
export interface ForumThreadDto {
  comment_count: number;
  _id: string;                        
  course_id: string;                   
  title: string;                        // Thread title
  description: string;                  // Thread description/content
  is_active: boolean;                   // Whether thread is active
  is_deleted: boolean;                  // Soft delete flag
  is_pinned: boolean;                   // Whether thread is pinned
  is_locked: boolean;                   // Whether thread is locked
  created_by: string;                   // Creator user ID
  view_count: number;                   // Number of views
  discussion_count: number;             // Number of discussions/replies
  last_activity_at: Date;               // Last activity timestamp
  tags: string[];                       // Thread tags
  category?: string;                    // Thread category (optional)
  is_flagged: boolean;                  // Whether thread is flagged
  createdAt: Date;                      // Creation timestamp
  updatedAt: Date;                      // Last update timestamp
  creator?: ThreadCreator;              // Populated creator information
}

// Thread List Response DTO (matches backend flat structure)
export interface ForumThreadListResponseDto {
  threads: ForumThreadDto[];
  total: number;           // Total number of threads
  page: number;            // Current page number
  limit: number;           // Items per page
  total_pages: number;     // Total number of pages
}

// Create Thread DTO (matches CreateForumThreadDto from backend)
export interface CreateThreadDto {
  course_id: string;                    // Required: associated course
  title: string;                        // Required: thread title
  description: string;                  // Required: thread description/content
  category?: string;                    // Optional: thread category
  tags?: string[];                      // Optional: thread tags
  is_pinned?: boolean;                  // Optional: pin thread
}

// Update Thread DTO (matches UpdateForumThreadDto from backend)
export interface UpdateThreadDto {
  title?: string;                       // Optional: update title
  description?: string;                 // Optional: update description
  category?: string;                    // Optional: update category
  tags?: string[];                      // Optional: update tags
  is_active?: boolean;                  // Optional: toggle active status
  is_pinned?: boolean;                  // Optional: toggle pinned status
  is_locked?: boolean;                  // Optional: toggle locked status
}

// Get Threads Query Parameters (matches GetForumThreadsDto from backend)
export interface GetForumThreadsDto {
  course_id: string;                    // Required: course ID to filter threads
  category?: string;                    // Optional: filter by category
  tags?: string[];                      // Optional: filter by tags
  is_active?: boolean;                  // Optional: filter by active status
  is_pinned?: boolean;                  // Optional: filter by pinned status
  search_text?: string;                 // Optional: search in title/description
  page?: number;                        // Optional: page number (default: 1)
  limit?: number;                       // Optional: items per page (default: 10)
  sort_by?: ThreadSortBy;              // Optional: sort by latest/popular/most_discussed
}

// API Response Types
export type CreateThreadResponse = APIResponse<ForumThreadDto>;
export type GetThreadResponse = APIResponse<ForumThreadDto | null>;
export type UpdateThreadResponse = APIResponse<ForumThreadDto>;
export type DeleteThreadResponse = APIResponse<{ deleted: boolean; thread_id: string }>;
export type ThreadListResponse = APIResponse<ForumThreadListResponseDto>;

// Form Types (for React Hook Form)
export interface ThreadFormData {
  title: string;
  description: string;
  category?: string;
  tags?: string[];
  is_pinned?: boolean;
}

// Helper function to get status badge color for UI
export function getThreadStatusBadge(thread: ForumThreadDto): { className: string; label: string } {
  if (thread.is_pinned) {
    return { className: 'bg-blue-100 text-blue-700', label: 'Pinned' };
  }
  if (thread.is_locked) {
    return { className: 'bg-red-100 text-red-700', label: 'Locked' };
  }
  if (!thread.is_active) {
    return { className: 'bg-gray-100 text-gray-700', label: 'Inactive' };
  }
  if (thread.is_flagged) {
    return { className: 'bg-orange-100 text-orange-700', label: 'Flagged' };
  }
  return { className: 'bg-green-100 text-green-700', label: 'Active' };
}

// Helper function to format thread date
export function formatThreadDate(date: Date | string | undefined): string {
  // Handle undefined or null dates
  if (!date) {
    return 'Unknown';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Unknown';
  }

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInSeconds = diffInMs / 1000;
   if (diffInSeconds < 60) {
    return 'just now';
  }
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    const minutes = Math.floor(diffInMs / (1000 * 60));
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    const days = Math.floor(diffInDays);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
}

// Helper function to get author display name
export function getThreadAuthorName(thread: ForumThreadDto): string {
  if (thread.creator) {
    return `${thread.creator.given_name} ${thread.creator.family_name}`.trim();
  }
  return 'Unknown User';
}

// Helper function to get author initials for avatar
export function getThreadAuthorInitials(thread: ForumThreadDto): string {
  if (thread.creator) {
    const firstName = thread.creator.given_name || '';
    const lastName = thread.creator.family_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  return 'U';
}
